import { useEffect, useRef, useCallback } from 'react'
import debounce from 'lodash/debounce'
import isEmpty from 'lodash/isEmpty'
import { createRoot } from 'react-dom/client'
import { useAppSelector } from '@redux/hooks'
import { appendRowColHeaderAttributes } from '@utils/append-row-col-header-attributes'
import {
  beforeChangeUpdateCellTree,
  updateInvalidCellState,
  onTableClick,
} from 'handsontable/hooks'
import { UrlAudits } from 'handsontable/utils/codes.url-audits'
import { useHandsontable } from './hooks/useHandsontable'
import { useHandsontableBinder } from './hooks/useHandsontableBinder'
import { Header } from './renderer/header/header'
import {
  checkIfTableHasData,
  getContextMenu,
  getMaxRows,
  getDataForHotTable,
  customIsEmptyRow,
  updateCellsToReadOnly,
  specifyTabMove,
  BranchIntegrationObj,
} from './utils'
import './submission-table.global.css'
import { DatasetWithDatasetRows } from '@global/types'
import { DataContainer } from 'dataContainer/data-container'

let headerElementCache = {}

interface SubmissionTableProps {
  dataContainer: DataContainer
  dataLoaded: () => void
  dataset: DatasetWithDatasetRows | null
  isResubmit: boolean
  onInvalidCellsUpdated: (e: unknown) => void
  onUserChange: () => void
  setBinderRef: (ref: unknown) => void
  setTableRef: (ref: unknown) => void
  template: {
    integrations: { slug: string; mapping_fields: { alias: string } }[]
    template_fields: { governance_type: string; field_id: number }[]
  }
  setCopyStatus: (status: boolean) => void
  setPasteStatus: (status: boolean) => void
  setTableHasData?: (status: boolean) => void
}

interface CellPropertiesParams {
  row: number
  prop: string
  currentColumn: unknown
}

interface HotTable {
  getSettings: () => {
    binder?: unknown
    columns: unknown[]
  }
  getSelected: () => number[][]
  render: () => void
}

const emptyFn = () => {
  // do nothing
}

export const SubmissionTable = (props: SubmissionTableProps) => {
  const {
    dataContainer,
    dataLoaded = () => undefined,
    dataset,
    isResubmit = false,
    onInvalidCellsUpdated,
    onUserChange,
    setBinderRef,
    setTableRef,
    template = null,
    setCopyStatus = emptyFn,
    setPasteStatus = emptyFn,
    setTableHasData = emptyFn,
  } = props

  const duplicatedRows = useAppSelector(
    (state: { Datasets: { duplicatedRows: unknown } }) => state.Datasets.duplicatedRows,
  )
  const permissions = useAppSelector(state => state.Auth.user?.permissions)
  const isAdmin = Boolean(permissions?.includes('all_privileges'))
  const isMounted = useRef(false)

  const hotTableData = getDataForHotTable(dataContainer)

  const { handsontableInstance, isReady, getHandsontableInstance, getHotTableContainerRef } =
    useHandsontable({
      onTableClick: (e: MouseEvent, ref: HotTable) => {
        onTableClick(e, ref, dataContainer)
      },
      setParentComponentRef: setTableRef,
      handsonTableOptions: {
        contextMenu: getContextMenu({
          isResubmit,
          dataContainer,
          setCopyStatus,
          isAdmin,
        }),
        minRows: isResubmit ? 0 : 50,
        minSpareRows: isResubmit ? 0 : 10,
        maxRows: getMaxRows(dataContainer.data as object[], dataset, isResubmit),
      },
    })

  useHandsontableBinder({
    afterBindCallback,
    dataContainer,
    getHandsontableInstance,
    getHotTableContainerRef,
    binderOptions: {
      resubmit: isResubmit,
      checkForInvalid: () => {
        changeCheck(getHandsontableInstance())
      },
      setPasteStatus,
      setCopyStatus,
    },
    setParentComponentRef: setBinderRef,
    isReady,
    updateSettings: (bind, settingsTableInstance, tableContainerDOM) => ({
      allColumns: bind.allRenderColumns,
      allColumnsByFieldId: bind.allRenderColumnsByFieldId,
      autoRowSize: true,
      binder: bind,
      columns: bind.visibleRenderColumns,
      colWidths: bind.visibleRenderColumns.map(col => {
        return col.governance_type === 'link-validation' ? 250 : 200
      }),
      data: hotTableData,
      dataSchema: dataContainer.getDataSchema() as unknown,
      fillHandle: { autoInsertRow: !isResubmit },
      afterGetColHeader: (colIdx: number, th: HTMLElement) => {
        if (settingsTableInstance) {
          customAfterGetColHeader(colIdx, th, settingsTableInstance as HotTable)
        }
      },
      afterGetRowHeader: (rowIdx, th) => {
        appendRowColHeaderAttributes(th, null, rowIdx)
      },
      afterColumnResize: (_, column) => {
        if (!settingsTableInstance) return
        const columnsWidth = settingsTableInstance.getSettings().colWidths
        if (!columnsWidth || typeof columnsWidth === 'number' || typeof columnsWidth === 'function')
          return
        const isLastColumn = columnsWidth.length - 1 === column

        const scrollableContainer = tableContainerDOM?.querySelector('.wtHolder')

        if (scrollableContainer && isLastColumn) {
          setTimeout(() => {
            scrollableContainer.scrollLeft =
              scrollableContainer.scrollWidth - scrollableContainer.clientWidth
          }, 0)
        }

        settingsTableInstance.getColHeader()
      },
      afterChange: (changes: unknown) => {
        if (changes) {
          changeCheck(settingsTableInstance) // Kickoff validation checks after a change.
        }
        if (settingsTableInstance) {
          checkIfTableHasData(settingsTableInstance, setTableHasData)
        }
      },
      afterRender: () => {
        if (settingsTableInstance) {
          checkIfTableHasData(settingsTableInstance, setTableHasData)
        }
      },
      afterRemoveRow: () => {
        changeCheck(settingsTableInstance)
      },
      afterDocumentKeyDown: async (event: KeyboardEvent) => {
        if (event.code !== 'KeyV') return

        // Catch thrown error from clipboard readText,
        // one way this occurs is if a user has blocked clipboard access.
        try {
          const text = await navigator.clipboard.readText()

          if (text) {
            setTableHasData(true)
          }
        } catch (error) {
          console.error(`Failed to copy afterDocumentKeyDown; ${String(error)}`)
        }
      },
      afterLoadData: () => {
        dataLoaded()
      },
      beforeChange: (changes: unknown, source: unknown) => {
        beforeChangeUpdateCellTree(changes, source, settingsTableInstance)
      },
      isEmptyRow: (rowNum: number) =>
        settingsTableInstance ? customIsEmptyRow(rowNum, settingsTableInstance) : false,
      tabMoves: (event: KeyboardEvent) =>
        settingsTableInstance ? specifyTabMove(event, settingsTableInstance) : undefined,
      cells: (row: number, col: number, prop: string) =>
        getCellProperties({ row, prop, currentColumn: bind.visibleRenderColumns[col] }),
    }),
  })

  const handleResize = useCallback(
    debounce(function handleResize() {
      if (handsontableInstance?.isDestroyed) return // Some renders are between instance setup - wait for it.

      const boundingClientRect = document.getElementById('HOTContainer')?.getBoundingClientRect()
      if (boundingClientRect && isMounted.current) {
        handsontableInstance?.updateSettings({
          width: boundingClientRect.width,
          height: boundingClientRect.height,
        })
      }
    }, 250),
    [],
  )

  // update invalid cell states
  // setup Handsontable binder, with settings
  // dataContainer updates: Should this be happening in a parent component?
  // window.onpopstate is being set to null cleaning up what's being set on useOverlay
  // and setTimeout is to remove it from the main thread and run after it closes the popup

  useEffect(() => {
    return () => {
      isMounted.current = false
      headerElementCache = {}
      window.removeEventListener('resize', handleResize)
      setTimeout(() => (window.onpopstate = null), 0)
    }
  }, [])

  function afterBindCallback() {
    const tableInstance = getHandsontableInstance()

    updateInvalidCellState(tableInstance, dataContainer, onInvalidCellsUpdated)

    const dcChangeCallback = () => {
      updateInvalidCellState(tableInstance, dataContainer, onInvalidCellsUpdated)

      // FIXME: Check if "dc" is actually needed from some use-case.
      const data = getDataForHotTable(dataContainer)

      // Add safety check before updating to prevent errors with destroyed instances
      if (tableInstance && !tableInstance.isDestroyed) {
        tableInstance.updateData(data)
      }
    }

    dataContainer.changeCallback = dcChangeCallback // Before calling loadDataFromDataset

    if (dataset?.dataset_rows) {
      dataContainer.loadDataFromDataset(dataset)
    }

    if (duplicatedRows) {
      const updatedDataset = {
        dataset_rows: duplicatedRows,
      }
      dataContainer.loadDataFromDataset(updatedDataset)
    }

    if (!isEmpty(dataset)) {
      UrlAudits.pollPending(dataset.uuid, dataContainer)
      UrlAudits.validateUncompleted(dataset, template, dataContainer)
    }
  }

  function changeCheck(hotTable) {
    updateInvalidCellState(hotTable, dataContainer, onInvalidCellsUpdated)
    onUserChange()
  }

  function customAfterGetColHeader(colIdx: number, th: HTMLElement, hotTable: HotTable) {
    appendRowColHeaderAttributes(th, colIdx, null)

    const column = hotTable.getSettings().columns[colIdx]
    const cachedItem = headerElementCache[colIdx] as unknown
    const isCached = !!cachedItem
    const inner = th.getElementsByTagName('div')[0]

    if (!column) {
      const holder = document.createElement('div')
      const root = createRoot(holder)
      root.render(<Header column={column} hotTable={hotTable} colIdx={colIdx} />)
      if (inner) {
        th.replaceChild(holder, inner)
      }
      return
    }

    if (!isCached) {
      const holder = document.createElement('div')
      const root = createRoot(holder)
      root.render(<Header column={column} hotTable={hotTable} colIdx={colIdx} />)
      headerElementCache[colIdx] = holder
    }

    // TH missing from node during re-render can cause TH to be
    // removed from DOM. This check prevents that.
    if (document.body.contains(th) && inner && headerElementCache[colIdx]) {
      th.replaceChild(headerElementCache[colIdx] as Node, inner)
    }
  }

  function getCellProperties({ row, prop, currentColumn }: CellPropertiesParams) {
    const branchIntegrationObj = createBranchIntegrationObj()

    return {
      ...updateCellsToReadOnly(
        (currentColumn as { editor: unknown } | null) ?? null,
        row,
        prop,
        hotTableData,
        branchIntegrationObj,
      ),
    }
  }

  function createBranchIntegrationObj(): BranchIntegrationObj | null {
    const branchIntegration = template?.integrations.find(({ slug }) => slug === 'branch')

    if (!branchIntegration || template === null) return null

    return {
      alias: branchIntegration.mapping_fields.alias,
      deepLinkId:
        template.template_fields.find(({ governance_type }) => governance_type === 'deep-link')
          ?.field_id ?? 0,
      datasetRows: dataset?.dataset_rows ?? [],
    }
  }

  window.addEventListener('resize', handleResize)

  return <div className="submission-table" />
}
